import React, {
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import Modal from "Components/Generic/Modal";
import { ItemJob } from "Models/sales";

import {
  BingoSheetComponent,
  BingoSheetFormType,
  buildManualInventoryItemJobs,
  isManualInventoryFormValid,
} from "./BingoSheet";
import { UploadPDFFile } from "./UploadPDFFile";
import UISteps from "Components/Generic/UISteps";
import { Button } from "Components/Form";
import { Color } from "Components/Form/Button";
import { JobInventoryPDF } from "Models/job.inventory.pdf";

enum Step {
  UPLOAD_INVENTORY_PDF = "upload",
  MANUAL_ITEMS = "manual-items",
}

interface Props {
  /** Parent merges: replaces the last manual bingo batch and appends this new batch. */
  onSubmit: (newManualBatchItems: ItemJob[]) => void;
  onSubmitPdfFile?: (inventoryPdf: JobInventoryPDF | null) => void;
  inventoryPdf?: JobInventoryPDF | null;
  /** Restored when opening the modal so the user can edit the last submitted manual batch values. */
  initialManualForm?: BingoSheetFormType | null;
  /** Called after a successful manual submit so the parent can persist the form for the next open. */
  onPersistManualForm?: (form: BingoSheetFormType) => void;
  /** When true, copy reflects edit (replace batch) rather than first-time create. */
  isEditingManualBatch?: boolean;
}

export interface PropsHandler {
  /** @deprecated Count unused; kept for call-site compatibility. */
  openModal: (existentItemJob?: number) => void;
  closeModal: () => void;
}

const CreateActualItemPdfModalComponent: ForwardRefRenderFunction<PropsHandler, Props> = (
  {
    onSubmit,
    onSubmitPdfFile,
    initialManualForm,
    onPersistManualForm,
    isEditingManualBatch = false,
    ...rest
  }: Props,
  ref
) => {
  const { t: translate } = useTranslation();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [bingoSheetForm, setBingoSheetForm] = useState<BingoSheetFormType | null>(null);

  const [currentStep, setCurrentStep] = useState<Step>(Step.UPLOAD_INVENTORY_PDF);

  const handleCloseModal = useCallback(() => {
    // Do not call onSubmitPdfFile(null) here — PDF is already applied on drop/select via
    // UploadPDFFile; clearing on close wiped the file and broke "View Inventory".
    setBingoSheetForm(null);
    setOpenModal(false);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openModal: (_existentItemJob?: number) => {
        setBingoSheetForm(initialManualForm ? { ...initialManualForm } : null);
        setOpenModal(true);
        setCurrentStep(Step.UPLOAD_INVENTORY_PDF);
      },
      closeModal: handleCloseModal,
    }),
    [initialManualForm, handleCloseModal]
  );

  const handleSaveItemJobs = () => {
    if (!bingoSheetForm || !isManualInventoryFormValid(bingoSheetForm)) return;
    onPersistManualForm?.({ ...bingoSheetForm });
    onSubmit(buildManualInventoryItemJobs(bingoSheetForm));
  };

  const manualItemsStepLabel = isEditingManualBatch
    ? translate("editManualItems")
    : translate("addManualItems");

  const steps = [
    {
      key: Step.UPLOAD_INVENTORY_PDF,
      label: translate("uploadPDF"),
      component: <UploadPDFFile {...rest} onSubmitPdfFile={onSubmitPdfFile} />,
    },
    {
      key: Step.MANUAL_ITEMS,
      label: manualItemsStepLabel,
      component: (
        <BingoSheetComponent.Form
          bingoSheetForm={bingoSheetForm}
          setBingoSheetForm={setBingoSheetForm}
        />
      ),
    },
  ];

  const handleNextStep = () => {
    if (currentStep === Step.UPLOAD_INVENTORY_PDF) {
      setCurrentStep(Step.MANUAL_ITEMS);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === Step.MANUAL_ITEMS) {
      setCurrentStep(Step.UPLOAD_INVENTORY_PDF);
    }
  };

  const onHandleSave = () => {
    if (currentStep === Step.UPLOAD_INVENTORY_PDF) {
      return handleNextStep();
    }
    return handleSaveItemJobs();
  };

  const manualStepValid = isManualInventoryFormValid(bingoSheetForm);
  const isManualStep = currentStep === Step.MANUAL_ITEMS;

  return (
    <Modal
      width={1040}
      open={openModal}
      handleClose={handleCloseModal}
      title={
        isEditingManualBatch
          ? translate("editInventoryManualTitle")
          : translate("createInventory")
      }
      showActionButtons
      showLeftActionButtons
      customLeftButtons={
        isManualStep ? (
          <Button variant="text" onClick={handlePreviousStep} customColor={Color.CANCEL}>
            {translate("back")}
          </Button>
        ) : (
          <Button onClick={handleCloseModal} customColor={Color.CANCEL}>
            {translate("cancel")}
          </Button>
        )
      }
      actionButtonProps={{
        onSave: onHandleSave,
        showSaveButton: true,
        saveDisabled: currentStep === Step.MANUAL_ITEMS && !manualStepValid,
        onCancel: handlePreviousStep,
        showCancelButton: false,
        textSave:
          currentStep === Step.MANUAL_ITEMS
            ? isEditingManualBatch
              ? translate("saveChanges")
              : translate("create")
            : translate("next"),
      }}
    >
      <UISteps
        stepsOption={[Step.UPLOAD_INVENTORY_PDF, Step.MANUAL_ITEMS]}
        currentStep={currentStep}
        steps={steps}
      />
    </Modal>
  );
};

const CreateActualItemPdfModal = React.forwardRef(CreateActualItemPdfModalComponent);

export { CreateActualItemPdfModal };
